import axios from 'axios';

// Legacy screens use a mix of fetch and axios. Attach the current session token
// to this application's API calls in one place so protected write endpoints do
// not depend on every individual screen remembering the header.
const isApplicationApi = (rawUrl) => {
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (!url.pathname.startsWith('/api/')) return false;

    const configuredHost = process.env.REACT_APP_API_URL
      ? new URL(process.env.REACT_APP_API_URL, window.location.origin).hostname
      : null;

    return url.hostname === window.location.hostname
      || url.hostname === configuredHost
      || url.hostname === 'localhost'
      || url.hostname === '127.0.0.1'
      || url.hostname === '213.109.146.248';
  } catch (_error) {
    return false;
  }
};

const getToken = () => localStorage.getItem('token');

const AUTH_FAILURE_MESSAGES = new Set([
  'Требуется авторизация',
  'Недействительный токен',
  'Учетная запись отключена'
]);

const AUTH_FAILURE_CODES = new Set([
  'AUTH_REQUIRED',
  'AUTH_TOKEN_INVALID',
  'AUTH_ACCOUNT_DISABLED'
]);

let authRedirectInProgress = false;

const handleAuthenticationFailure = (status, data) => {
  if (![401, 403].includes(status) || !getToken()) return;

  const message = data?.error || data?.message;
  const isAuthenticationFailure = AUTH_FAILURE_CODES.has(data?.code)
    || AUTH_FAILURE_MESSAGES.has(message);
  if (!isAuthenticationFailure || authRedirectInProgress) return;

  authRedirectInProgress = true;
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  if (window.location.pathname !== '/login') {
    window.location.replace('/login?reason=session-expired');
  }
};

// If one tab discovers an invalid session, close the session in every other
// open tab as well instead of leaving stale screens available there.
window.addEventListener('storage', (event) => {
  if (event.key !== 'token' || event.newValue !== null || authRedirectInProgress) return;
  authRedirectInProgress = true;
  localStorage.removeItem('user');
  if (window.location.pathname !== '/login') {
    window.location.replace('/login?reason=session-expired');
  }
});

const normalizeLegacyApiUrl = (rawUrl) => {
  try {
    const url = new URL(rawUrl, window.location.origin);
    const isLegacyDirectPort = url.hostname === '213.109.146.248'
      && ['3001', '3002'].includes(url.port)
      && url.pathname.startsWith('/api/');
    if (!isLegacyDirectPort) return rawUrl;

    const configuredBase = new URL(process.env.REACT_APP_API_URL || window.location.origin, window.location.origin);
    return new URL(`${url.pathname}${url.search}${url.hash}`, configuredBase.origin).toString();
  } catch (_error) {
    return rawUrl;
  }
};

const originalFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  const rawUrl = typeof input === 'string' ? input : input.url;
  const normalizedUrl = normalizeLegacyApiUrl(rawUrl);
  const normalizedInput = normalizedUrl === rawUrl
    ? input
    : (typeof input === 'string' ? normalizedUrl : new Request(normalizedUrl, input));
  const token = getToken();
  if (!token || !isApplicationApi(normalizedUrl)) return originalFetch(normalizedInput, init);

  const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
  if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  const response = await originalFetch(normalizedInput, { ...init, headers });
  if ([401, 403].includes(response.status)) {
    response.clone().json()
      .then((data) => handleAuthenticationFailure(response.status, data))
      .catch(() => {});
  }
  return response;
};

axios.interceptors.request.use((config) => {
  const rawUrl = config.baseURL
    ? new URL(config.url || '', new URL(config.baseURL, window.location.origin)).toString()
    : config.url;
  const normalizedUrl = rawUrl ? normalizeLegacyApiUrl(rawUrl) : rawUrl;
  if (normalizedUrl && normalizedUrl !== rawUrl) {
    config.baseURL = undefined;
    config.url = normalizedUrl;
  }
  const token = getToken();
  if (token && normalizedUrl && isApplicationApi(normalizedUrl)) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    handleAuthenticationFailure(error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);
